import { useCallback, useEffect, useRef } from 'react'

/**
 * Guide playback telemetry.
 *
 * Optional throughout: a host that passes no `telemetry` prop records nothing.
 * That matters because this same reader renders the Console's approval preview
 * — an admin reviewing a guide before publishing it must not show up as a
 * client watching it.
 *
 * `watchedMs` is a DELTA since the previous report, not a cumulative total, and
 * every report carries an `eventId` so a retried or duplicated beacon is
 * discarded server-side instead of inflating watch time.
 */
export interface GuideProgressReport {
  /** The act/chapter being watched. */
  sequence: string
  /** Playhead position at the time of the report. */
  positionMs: number
  /** Milliseconds of playback since the previous report. */
  watchedMs: number
  /** Stable per-mount id, so one sitting can be reconstructed. */
  playbackId: string
  /** Unique per report — the server's idempotency key. */
  eventId: string
  /** Why the report fired. */
  reason: 'heartbeat' | 'pause' | 'ended' | 'sequence' | 'hidden'
}

export interface GuideTelemetry {
  /** Fired once when the reader mounts for a guide. */
  onOpen?: () => void
  /** Fired on a heartbeat, on pause/ended, on act change and on page hide. */
  onProgress?: (report: GuideProgressReport) => void
}

const HEARTBEAT_MS = 15_000

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Track watch time for one video element.
 *
 * Time is accumulated from the element's own `currentTime` between reports
 * rather than from wall-clock, so pausing, seeking and background tabs do not
 * invent watch time. A backwards seek contributes nothing.
 */
export function useVideoTelemetry(
  video: { current: HTMLVideoElement | null },
  sequence: string,
  telemetry: GuideTelemetry | undefined,
) {
  const playbackId = useRef<string>('')
  const lastPosition = useRef(0)
  const accumulated = useRef(0)

  if (!playbackId.current) playbackId.current = randomId()

  const flush = useCallback((reason: GuideProgressReport['reason']) => {
    const onProgress = telemetry?.onProgress
    if (!onProgress) return
    const watched = Math.round(accumulated.current)
    // An empty report says nothing, and every act of a guide now mounts at
    // once — so leaving the page would otherwise beacon one zero per act.
    // `ended` is the exception: reaching the end is worth recording even when
    // the last stretch was already reported.
    if (watched <= 0 && reason !== 'ended') return
    accumulated.current = 0
    onProgress({
      sequence,
      positionMs: Math.round((video.current?.currentTime ?? 0) * 1000),
      watchedMs: watched,
      playbackId: playbackId.current,
      eventId: randomId(),
      reason,
    })
  }, [sequence, telemetry, video])

  const onTimeUpdate = useCallback((currentTime: number) => {
    const deltaMs = (currentTime - lastPosition.current) * 1000
    lastPosition.current = currentTime
    // Only forward progress counts. A seek (either direction) or a loop is not
    // watch time, and `timeupdate` fires several times a second.
    if (deltaMs > 0 && deltaMs < 2000) accumulated.current += deltaMs
  }, [])

  const onSeek = useCallback((currentTime: number) => {
    lastPosition.current = currentTime
  }, [])

  // Heartbeat, plus the terminal flushes. Without the pagehide/visibilitychange
  // pair a tab closed mid-video reports nothing at all for that stretch.
  useEffect(() => {
    if (!telemetry?.onProgress) return
    const timer = setInterval(() => flush('heartbeat'), HEARTBEAT_MS)
    const onHide = () => flush('hidden')
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush('hidden') }
    window.addEventListener('pagehide', onHide)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(timer)
      window.removeEventListener('pagehide', onHide)
      document.removeEventListener('visibilitychange', onVisibility)
      flush('hidden')
    }
  }, [flush, telemetry])

  return { onTimeUpdate, onSeek, flush }
}

/** Fire `onOpen` exactly once per guide, not once per render. */
export function useGuideOpen(guideId: string, telemetry: GuideTelemetry | undefined) {
  const reported = useRef<string>('')
  useEffect(() => {
    if (!telemetry?.onOpen || reported.current === guideId) return
    reported.current = guideId
    telemetry.onOpen()
  }, [guideId, telemetry])
}
